import { Area } from 'react-easy-crop';
import { IImageSize } from 'types/components/inputs/input.interface';

const JPEG_MIME = 'image/jpeg';
const JPEG_QUALITY = 0.92;

const buildJpegFileName = (fileName: string): string =>
  `${fileName.replace(/\.[^/.]+$/, '')}.jpg`;

const canvasToJpegFile = (
  canvas: HTMLCanvasElement,
  fileName: string
): Promise<File> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to convert image to JPEG'));
          return;
        }

        resolve(new File([blob], buildJpegFileName(fileName), { type: JPEG_MIME }));
      },
      JPEG_MIME,
      JPEG_QUALITY
    );
  });

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const drawImageOnCanvas = (
  image: CanvasImageSource,
  width: number,
  height: number,
  sourceX = 0,
  sourceY = 0,
  sourceWidth?: number,
  sourceHeight?: number
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context is not available');
  }

  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  const resolvedSourceWidth =
    sourceWidth ??
    ('width' in image && typeof image.width === 'number' ? image.width : width);
  const resolvedSourceHeight =
    sourceHeight ??
    ('height' in image && typeof image.height === 'number'
      ? image.height
      : height);

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    resolvedSourceWidth,
    resolvedSourceHeight,
    0,
    0,
    width,
    height
  );

  return canvas;
};

export const normalizeImageToJpeg = async (imageFile: File): Promise<File> => {
  const url = URL.createObjectURL(imageFile);

  try {
    const image = await createImage(url);
    const canvas = drawImageOnCanvas(image, image.width, image.height);

    return canvasToJpegFile(canvas, imageFile.name);
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const getCroppedImg = async (
  imageFile: File,
  crop: Area,
  height = 500,
  width = 500
): Promise<File> => {
  const url = URL.createObjectURL(imageFile);

  try {
    const image = await createImage(url);
    const canvas = drawImageOnCanvas(
      image,
      width,
      height,
      crop.x,
      crop.y,
      crop.width,
      crop.height
    );

    return canvasToJpegFile(canvas, imageFile.name);
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const getImageSize = (height: number, width?: number): IImageSize => ({
  width: width ?? height,
  height,
});
